import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const Yield = () => {
  // State variables for Yield calculation
  const [totalCropWeight, setTotalCropWeight] = useState(0); // Total crop weight harvested
  const [areaPlanted, setAreaPlanted] = useState(0); // Area planted
  const [yieldResult, setYieldResult] = useState(0); // Calculated yield
  const [cropWeightUnit, setCropWeightUnit] = useState("kg"); // Unit for crop weight
  const [areaUnit, setAreaUnit] = useState("hectares"); // Unit for area

  // Event handlers to update state values
  const handleTotalCropWeightChange = (e) =>
    setTotalCropWeight(parseFloat(e.target.value) || 0);
  const handleAreaPlantedChange = (e) =>
    setAreaPlanted(parseFloat(e.target.value) || 0);

  // Event handlers to change units
  const handleCropWeightUnitChange = (e) => setCropWeightUnit(e.target.value);
  const handleAreaUnitChange = (e) => setAreaUnit(e.target.value);

  // Convert crop weight to kg or tons based on selected unit
  const convertCropWeight = (weight, unit) => {
    if (unit === "tons") {
      return weight * 1000; // 1 ton = 1000 kg
    }
    return weight; // Already in kg
  };

  // Convert area to hectares or acres based on selected unit
  const convertArea = (area, unit) => {
    if (unit === "acres") {
      return area * 2.471; // 1 hectare = 2.471 acres
    }
    return area; // Already in hectares
  };

  // useEffect hook to calculate yield when any input changes
  useEffect(() => {
    // Convert to standard units before calculating yield
    const cropWeightInKg = convertCropWeight(totalCropWeight, cropWeightUnit);
    const areaInHectares = convertArea(areaPlanted, areaUnit);

    // Calculate yield
    if (areaInHectares > 0 && cropWeightInKg > 0) {
      const yieldCalculation = (cropWeightInKg / areaInHectares) * 100; // Calculate yield per hectare
      setYieldResult(yieldCalculation);
    } else {
      setYieldResult(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [totalCropWeight, areaPlanted, cropWeightUnit, areaUnit]);

  return (
    <Layout title="Yield Calculator (Agriculture)" path="yield-calculator">
      <section className="section-padding" id="yield-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Yield Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Yield Calculator (Agriculture)</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Total Crop Weight Input with Unit Selection */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="totalCropWeight" className="form-label">
                        Total Crop Weight
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Total Crop Weight:</strong> Enter the harvested crop weight. Choose the unit (kg or tons) from the dropdown."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <div className="d-flex">
                        <input
                          type="number"
                          className="form-control"
                          id="totalCropWeight"
                          placeholder="Enter total crop weight"
                          value={totalCropWeight || ""}
                          onChange={handleTotalCropWeightChange}
                        />
                        <select
                          className="form-select ms-2"
                          value={cropWeightUnit}
                          onChange={handleCropWeightUnitChange}
                        >
                          <option value="kg">kg</option>
                          <option value="tons">tons</option>
                        </select>
                      </div>
                    </div>

                    {/* Area Planted Input with Unit Selection */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="areaPlanted" className="form-label">
                        Area Planted
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Area Planted:</strong> Enter the total area where crops were planted. Choose the unit (hectares or acres) from the dropdown."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <div className="d-flex">
                        <input
                          type="number"
                          className="form-control"
                          id="areaPlanted"
                          placeholder="Enter area planted"
                          value={areaPlanted || ""}
                          onChange={handleAreaPlantedChange}
                        />
                        <select
                          className="form-select ms-2"
                          value={areaUnit}
                          onChange={handleAreaUnitChange}
                        >
                          <option value="hectares">hectares</option>
                          <option value="acres">acres</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Yield:{" "}
                        <span className="fw-bold ms-3">
                          {yieldResult === 0
                            ? "0.00"
                            : yieldResult.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                          per unit area
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Yield Calculator (Agriculture)
                </h3>

                {/* What is a Yield Calculator? */}
                <h4 className="mt-3 mb-3">What is a Yield Calculator?</h4>
                <p>
                  The **Yield Calculator** is designed to help farmers and
                  agricultural businesses assess crop yield per unit area. By
                  calculating the productivity of a given field or plot, users
                  can make more informed decisions on how to enhance farming
                  practices and optimize outputs.
                </p>

                {/* Why Use a Yield Calculator? */}
                <h4 className="mt-4 mb-3">Why Use a Yield Calculator?</h4>
                <p>
                  Accurate yield calculations are essential for evaluating the
                  success of current farming techniques and identifying areas
                  for improvement. It helps farmers understand how efficiently
                  they are using their resources and whether adjustments are
                  necessary to boost productivity.
                </p>

                {/* Benefits of Using a Yield Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using a Yield Calculator
                </h4>
                <ul>
                  <li>
                    <strong>Helps optimize farming techniques:</strong> By
                    comparing yield results over time, farmers can improve their
                    practices.
                  </li>
                  <li>
                    <strong>
                      Provides insight into crop performance and potential
                      revenue:
                    </strong>{" "}
                    Yield is a direct indicator of financial success and can aid
                    in estimating earnings.
                  </li>
                  <li>
                    <strong>Supports strategic planning:</strong> Guides future
                    resource allocation and crop management strategies.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use a Yield Calculator?
                </h4>
                <p>The Yield Calculator is perfect for:</p>
                <ul>
                  <li>
                    <strong>Farmers:</strong> To gauge productivity and refine
                    agricultural practices.
                  </li>
                  <li>
                    <strong>Agricultural consultants:</strong> For advising
                    clients on crop and resource management.
                  </li>
                  <li>
                    <strong>Agri-businesses:</strong> To monitor large-scale
                    operations and make strategic decisions.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Yield;
